import { PlateProvider } from '@udecode/plate';
import React, { useMemo } from 'react';

import { Attachment, Emoji } from 'components/composer/shared/editor_action_buttons_new';
import useForgottenTextModal from 'components/composer/email/use_forgotten_text_modal';
import ForgottenAttachmentModal from 'components/composer/email/forgotten_attachment_modal';
import ForgottenTextModal from 'components/composer/email/forgotten_text_modal';
import { BoldItalicUnderlineControls, ControlsWrapper } from 'components/composer/shared/editor_controls_new';
import { EditorContainer } from './shared/editor_new';
import { SubjectContainerNew } from './email/subject';
import Composer, { Header, HeaderText } from './shared/composer_new';
import ComposerEditor from './shared/composer_editor';
import connect from 'components/lib/connect';
import From from './email/from';
import getInitialHtml from 'components/composer/email/get_initial_html';
import InternalEmailModal from './email/internal_email_modal_wrapper';
import Recipients from './email/recipients';
import SelectionInitializer from './email/selection_initializer';
import SendEmail from 'actions/composer/send_email';
import serializePlaintext from 'components/text_editor_new/lib/serialize_plaintext';
import Thread from './email/thread';
import useComposer from './shared/use_composer';
import useEditor from './shared/use_editor';
import useHasRemovableThread from './email/use_has_removable_thread';
import useInitialValue from 'components/text_editor_new/hooks/use_initial_value';
import usePlaintextEditorPlugins from 'components/text_editor_new/hooks/use_plaintext_editor_plugins';
import useRichTextEditorPlugins from 'components/text_editor_new/hooks/use_rich_text_editor_plugins';
import useWasCustomerAddedToEmail from './email/use_was_customer_added_to_email';

export function EmailComposer({ defaultBody, profile }) {
  const composerProps = useComposer({
    action: SendEmail,
    options: { createContent },
  });
  const editorProps = useEditor('bodyHtml', composerProps);
  const subjectEditorProps = useEditor('subjectHtml', composerProps, {
    attr: 'subject',
    dontAutofocus: true,
    serializer: serializePlaintext,
    editorId: 'subjectHtml',
  });
  const hasRemovableThread = useHasRemovableThread();
  const [wasCustomerAddedToEmail, ignoreCustomerWasAddedToEmail] = useWasCustomerAddedToEmail(profile);

  const editorRef = useRichTextEditorPlugins(composerProps, editorProps);
  const subjectRef = usePlaintextEditorPlugins(composerProps, subjectEditorProps);

  // With greetings and signatures, we want to place the initial cursor position in an expected place for agents..
  // if we have a greeting _and_ a signature, for example, the cursor goes in the middle. If we're loading a draft,
  // on the other hand, the cursor will just go at the end (hence the -1).
  const [initialHtml, initialParagraphIndex] = useMemo(() => {
    if (editorProps.initialHtml) {
      return [editorProps.initialHtml, -1];
    }

    return getInitialHtml(defaultBody);
  }, []);

  const initialSubjectValue = useInitialValue(subjectEditorProps.initialHtml, subjectRef.current);

  const {
    onSubmit: decoratedOnSubmit,
    showForgottenTextModal,
    clearShowForgottenTextModal,
    forgottenAttachmentText,
    clearForgottenAttachmentText,
  } = useForgottenTextModal(composerProps.onSubmit, editorProps.editorRef.current);

  return (
    <Composer
      defaultEditor={editorRef.current}
      defaultEditorId={editorProps.editorId}
      defaultOnChange={editorProps.onChange}
      includeAttachments
      initialHtml={initialHtml}
      submitText="Send Email"
      {...composerProps}
      onSubmit={decoratedOnSubmit}
    >
      {initialParagraphIndex > -1 ? <SelectionInitializer paragraphIndex={initialParagraphIndex} /> : null}
      <Header>
        <HeaderText>Email</HeaderText>
        <Recipients />
        <From />
      </Header>
      <SubjectContainerNew editorProps={subjectEditorProps}>
        <PlateProvider
          editor={subjectRef.current}
          id={subjectEditorProps.editorId}
          initialValue={initialSubjectValue}
          onChange={subjectEditorProps.onChange}
        >
          <ComposerEditor
            autoFocus={false}
            minHeight="0px"
            placeholder="Enter a subject"
            shouldNotUseAnswers
            width="100%"
            {...subjectEditorProps}
          />
        </PlateProvider>
        <ControlsWrapper>
          <BoldItalicUnderlineControls {...editorProps} />
          <Emoji {...editorProps} />
          <Attachment alwaysShow />
        </ControlsWrapper>
      </SubjectContainerNew>
      <EditorContainer includeAttachments>
        <ComposerEditor
          autoFocus={!!editorProps.initialHtml || initialParagraphIndex === -1}
          placeholder="Type to respond"
          {...editorProps}
          initialHtml={initialHtml}
        />
        {hasRemovableThread && <Thread />}
      </EditorContainer>
      {hasRemovableThread && wasCustomerAddedToEmail ? (
        <InternalEmailModal onKeepHistory={ignoreCustomerWasAddedToEmail} />
      ) : null}
      {forgottenAttachmentText ? (
        <ForgottenAttachmentModal
          onCancel={clearForgottenAttachmentText}
          onSubmit={composerProps.onSubmit}
          text={forgottenAttachmentText}
        />
      ) : null}
      {showForgottenTextModal ? (
        <ForgottenTextModal onCancel={clearShowForgottenTextModal} onSubmit={composerProps.onSubmit} />
      ) : null}
    </Composer>
  );
}

const MemoizedComposer = React.memo(EmailComposer);
const EmailComposerContainer = connect(mapStateToProps)(MemoizedComposer);

function mapStateToProps({ getProvider }) {
  const currentAgent = getProvider('currentAgent').get() || {};
  const { emailGreetingSnippetId, emailSignatureSnippetId } = currentAgent;

  return {
    defaultBody: {
      greetingSnippet: getProvider('currentAgentSnippets').findBy({ id: emailGreetingSnippetId }),
      signatureSnippet: getProvider('currentAgentSnippets').findBy({ id: emailSignatureSnippetId }),
    },
    profile: getProvider('profile').get(),
  };
}

export default EmailComposerContainer;

function createContent(editor, attr, content) {
  if (attr === 'bodyHtml') {
    return {
      ...content,
      bodyPlain: serializePlaintext(editor),
    };
  }

  return content;
}
